import { Ban, Pencil } from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatDate } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { StatusPill } from '../../../../components/StatusPill'
import { InsurancePolicy as IInsurancePolicy, PolicyStatus } from '../../../../models/insurancePolicy'
import { CorgiPolicyLinks } from './CorgiPolicyLinks'
import { PolicyCoiModeSetting } from './PolicyCoiModeSetting'
import { PolicyCoverageRow } from './PolicyCoverageRow'
import { PolicyCoverageSummary } from './PolicyCoverageSummary'
import { PolicyDocuments } from './PolicyDocuments'
import { PolicyPremiumReceipt } from './PolicyPremiumReceipt'

interface InsurancePolicyProps {
  policy: IInsurancePolicy
  /**
   * Whether to show the policy documents section. Plain org members (role USER)
   * have no documents tab, and the policy PDF is off-limits to them here too —
   * required rather than optional so a new call site has to make the call.
   */
  canViewDocuments: boolean
  /**
   * Whether to offer the automatic/manual COI switch. Cai admins only, and
   * decided by the caller — the card stays presentational, so rendering it
   * needs no query client.
   */
  canEditCoiMode?: boolean
  /** Staff see which wholesaler the policy came through; customers see the carrier only. */
  isStaff?: boolean
  onEditPolicy?: () => void
  /** Staff only: opens the cancellation dialog. Omitted where the policy can't be canceled. */
  onCancelPolicy?: () => void
  onUploadPolicyDoc?: () => void
  onDeletePolicyDoc?: (attachmentId: string) => void
  onGenerateSummary?: () => void
  /** Staff-only strip across the top of the card, edge to edge, such as the new policy checklist. */
  banner?: ReactNode
}

const POLICY_STATUS_MAP: Record<PolicyStatus, string> = {
  [PolicyStatus.ACTIVE]: 'active',
  [PolicyStatus.UPCOMING]: 'upcoming',
  [PolicyStatus.EXPIRING]: 'underwriting',
  [PolicyStatus.EXPIRED]: 'expired',
  [PolicyStatus.INACTIVE]: 'inactive',
  [PolicyStatus.CANCELING]: 'canceling',
  [PolicyStatus.CANCELED]: 'canceled',
}

export function InsurancePolicy({
  policy,
  canViewDocuments,
  canEditCoiMode,
  isStaff,
  onEditPolicy,
  onCancelPolicy,
  onUploadPolicyDoc,
  onDeletePolicyDoc,
  onGenerateSummary,
  banner,
}: InsurancePolicyProps) {
  const showIcon =
    policy.status === PolicyStatus.ACTIVE ||
    policy.status === PolicyStatus.UPCOMING ||
    policy.status === PolicyStatus.CANCELING

  return (
    <Card className={cn(banner && 'overflow-hidden pt-0')}>
      {banner}
      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {showIcon && policy.iconUrl && (
              <img src={policy.iconUrl} alt={policy.iconAlt} className="h-12 w-12 object-contain" />
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-black">{policy.vendorName}</span>
                <StatusPill status={POLICY_STATUS_MAP[policy.status]} />
              </div>
              {isStaff && policy.broker && (
                <Badge variant="outline" className="w-fit text-xs">
                  via {policy.broker}
                </Badge>
              )}
              <span className="text-muted-foreground text-md font-medium">
                Policy {policy.vendorId || policy.idShort} · {formatDate(policy.dateStart)} –{' '}
                {formatDate(policy.dateEnd)}
              </span>
              {policy.status === PolicyStatus.CANCELING && (
                <span className="text-warning-d1 text-md font-medium">
                  Cancels on {formatDate(policy.dateCanceled)}
                </span>
              )}
              {policy.status === PolicyStatus.CANCELED && (
                <span className="text-error text-md font-medium">
                  Canceled as of {formatDate(policy.dateCanceled)}
                </span>
              )}
              <CorgiPolicyLinks links={policy.corgiPolicyLinks ?? []} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {canEditCoiMode && <PolicyCoiModeSetting policy={policy} />}
            {onCancelPolicy && (
              <Button size="sm" variant="outline" onClick={onCancelPolicy}>
                <Ban className="h-4 w-4" />
                Cancel policy
              </Button>
            )}
            {onEditPolicy && (
              <Button size="sm" aria-label="Edit policy" onClick={onEditPolicy}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </div>

        <Separator />

        <div className="flex flex-col gap-4">
          {policy.coverages.map((coverage) => (
            <PolicyCoverageRow key={coverage.id} coverage={coverage} />
          ))}
        </div>

        <Separator />

        <PolicyPremiumReceipt policy={policy} />

        {policy.summary && (
          <>
            <Separator />
            <PolicyCoverageSummary summary={policy.summary} />
          </>
        )}

        {canViewDocuments && (
          <>
            <Separator />

            <PolicyDocuments
              policy={policy}
              onUploadPolicyDoc={onUploadPolicyDoc}
              onDeletePolicyDoc={onDeletePolicyDoc}
              onGenerateSummary={onGenerateSummary}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
